/**
 * A form somebody was sent a link to.
 *
 * Unauthenticated, and deliberately so: a form's whole purpose is to be filled
 * in by people who have no account here. It goes through the bare client for
 * that reason -- the session middleware would try to refresh a token that does
 * not exist, and a visitor would be sent to the sign-in page for asking to
 * fill in a form.
 */
import { queryOptions } from '@tanstack/react-query'

import { getBareApi, unwrap } from '@/api/client'
import type { Field } from '@/api/types'
import { qk } from '@/api/query-keys'
import type { ConditionalOptions } from './conditions'

/**
 * One field on a form.
 *
 * `field` is typed by the generator as an id, a name and a type, because the
 * serializer that describes it is chosen at runtime per field type -- the
 * schema says so in as many words. What actually arrives is the field's own
 * serialisation, which is what makes the editors work here at all, so it is
 * cast at this boundary the way a row is.
 */
export interface FormField extends ConditionalOptions {
  name: string
  description?: string
  required?: boolean
  order?: number
  field: Field
}

export interface PublicForm {
  title?: string
  intro?: string
  submit_text?: string
  fields: FormField[]
}

/** What the backend says to do once a form has been filled in. */
export interface FormSubmitted {
  submit_action?: string
  submit_action_message?: string
  submit_action_redirect_url?: string
  row_id: number
}

export function formQuery(slug: string) {
  return queryOptions({
    queryKey: qk.form(slug),
    // A form is a public page somebody may sit on for a while; refetching it
    // under them would rearrange the fields they are filling in.
    staleTime: Infinity,
    retry: false,
    queryFn: async (): Promise<PublicForm> => {
      const form = await unwrap<unknown>(
        getBareApi().GET('/api/database/views/form/{slug}/submit/', {
          params: { path: { slug } },
        }),
      )
      return form as PublicForm
    },
  })
}

/** Sends the filled-in form, and answers with what to do next. */
export async function submitForm(
  slug: string,
  values: Record<string, unknown>,
): Promise<FormSubmitted> {
  const answer = await unwrap<unknown>(
    getBareApi().POST('/api/database/views/form/{slug}/submit/', {
      params: { path: { slug } },
      // Cast for the reason every row payload is: the generated body is one
      // fictional table's, and no real row fits it.
      body: values as never,
    }),
  )
  return answer as FormSubmitted
}

/** The fields a form shows, in the order it shows them. */
export function formFields(form: PublicForm): FormField[] {
  return [...form.fields].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
}
